import React, { useState } from 'react';
import { Modal, Skeleton, Spin, Tooltip } from 'antd';
import {
    CubeIcon,
    CurrencyDollarIcon,
    FloppyDiskIcon,
    GaugeIcon,
    InfoIcon,
    LightningIcon,
    PercentIcon,
    SparkleIcon,
    SunIcon,
    TreeEvergreenIcon,
} from '@phosphor-icons/react';
import { formatLayoutNotSellableReason } from '../utils/layout';
import '../oneButton-css/AiCatalogCardsDesign.css';

export type DashboardAiCatalogListRowStatusVariant = 'amber' | 'muted' | 'lime' | 'neutral';

export type DashboardAiCatalogListRowProps = {
    theme: 'dark' | 'light';
    rowIndex: number;
    cardKey: string;
    /** Roof / layout snapshot; when set, shown inside thumb */
    snapshotUrl?: string;
    /** Shown inside the thumb when snapshotUrl is missing (e.g. capture failure). */
    snapshotError?: string;
    snapshotLoading?: boolean;
    /** When true, show a “Not sellable” badge beside status (never on the thumbnail). */
    showNotSellableStamp?: boolean;
    /** Tooltip explaining why the design is not sellable (shown beside the badge). */
    notSellableReason?: string;
    label: string;
    summaryLine: string;
    prodLine: string;
    /** Optional second line (e.g. efficiency · solar) */
    detailLine?: string;
    statusLabel: string;
    statusVariant: DashboardAiCatalogListRowStatusVariant;
    systemKw: string;
    pricingLine?: string;
    pricingContent?: React.ReactNode;
    isSelected: boolean;
    isSimulating: boolean;
    selectForPricingDisabled?: boolean;
    pricingBusy?: boolean;
    pricingDisabled?: boolean;
    onSelectForPricing: () => void;
    /** Name click: select for pricing and load layout in 3D (falls back to onSelectForPricing only). */
    onActivateDesign?: () => void;
    onPricing: () => void;
    onOpen3D: () => void;
    /** Extra icon buttons (dashboard: check, tree, wishlist, variants, …) */
    extraActions?: React.ReactNode;
    /** e.g. inline “Selected” badge */
    trailing?: React.ReactNode;
    /** Wider dashboard rail */
    variant?: 'rail' | 'dashboard';
    /** When no snapshot, which icon to show in the thumb */
    emptyThumb?: 'sparkle' | 'floppy' | 'tree';
    disqualified?: boolean;
    /** Hide the financing ($) control (e.g. original site row) */
    hideDollarAction?: boolean;
    /** Show EPC comparison button (dashboard AI catalog cards). */
    showEpcAction?: boolean;
    epcBusy?: boolean;
    onOpenEpc?: () => void;
    /** Hide the kW metric in the tail (e.g. reference rows with no size) */
    hideSystemKw?: boolean;
    /** Hide trailing chevron (e.g. tree / tool rows) */
    hideChevron?: boolean;
    /** When false, hide the numeric index overlay on the thumbnail. Defaults off for dashboard cards, on for rail. */
    showThumbIndex?: boolean;
    /** Show spinner on the 3D control while applying */
    open3dBusy?: boolean;
    /** In dashboard layout, hide the entire left thumbnail column */
    hideThumb?: boolean;
    /** Tree-cutting modal: render supplement chips under the thumbnail instead of in the card body */
    supplementsBelowThumb?: boolean;
};

function pillClass(variant: DashboardAiCatalogListRowStatusVariant): string {
    if (variant === 'amber') return ' obd-ai-catalog-list__pill--amber';
    if (variant === 'muted') return ' obd-ai-catalog-list__pill--muted';
    if (variant === 'lime') return ' obd-ai-catalog-list__pill--lime';
    return '';
}

function compactSummaryText(text: string): string {
    return text.replace(/\bmodules\b/gi, 'mod');
}

function splitSummaryWithTreeCount(text: string): { baseText: string; treeCount: string | null } {
    const m = text.match(/^(.*?)(?:\s*·\s*)([^·]+)\s*trees?\s*$/i);
    if (!m) return { baseText: text, treeCount: null };
    const countMatch = m[2].match(/-?\d+(?:\.\d+)?|—/);
    return { baseText: m[1].trim(), treeCount: countMatch ? countMatch[0] : m[2].trim() };
}

function compactStatsText(text?: string): string | undefined {
    if (!text) return text;
    return text
        .replace(/Solar Access/gi, 'Solar Access')
        .replace(/Efficiency/gi, 'Efficiency')
        .replace(/Multiplier/gi, 'Multiplier');
}

function splitCompactStatsLines(text?: string): string[] {
    if (!text) return [];
    return text
        .replace(/\s+Efficiency:/g, '\nEfficiency:')
        .replace(/\s+Multiplier:/g, '\nMultiplier:')
        .split('\n')
        .map((s) => s.trim())
        .filter(Boolean);
}

function splitDashboardDetailLines(text?: string): string[] {
    if (!text) return [];
    return text
        .replace(/\s+Efficiency:/gi, '\nEfficiency:')
        .replace(/\s+Multiplier:/gi, '\nMultiplier:')
        .split(/\s*·\s*|\n/)
        .map((s) => s.trim())
        .filter(Boolean);
}

type DashboardDetailLineKind = 'partner-hint' | 'perf' | 'supplement';

function classifyDashboardDetailLine(line: string): DashboardDetailLineKind {
    const t = line.trim();
    if (/^partner\s+pricing\b/i.test(t) || /use \$ then see/i.test(t)) return 'partner-hint';
    if (/^(solar access|efficiency|multiplier)\s*:/i.test(t)) return 'perf';
    return 'supplement';
}

function formatDashboardPerfLabel(raw: string): string {
    const t = raw.trim();
    if (/^(?:sa|solar\s*access)$/i.test(t)) return 'Solar Access';
    if (/^(?:eff(?:iciency)?)$/i.test(t)) return 'Efficiency';
    if (/^(?:mult(?:iplier)?|multiplier)$/i.test(t)) return 'Multiplier';
    return t;
}

function parseDashboardStatLine(line: string): { label: string; value: string } {
    const colonIdx = line.indexOf(':');
    if (colonIdx >= 0) {
        return {
            label: formatDashboardPerfLabel(line.slice(0, colonIdx).trim()),
            value: line.slice(colonIdx + 1).trim(),
        };
    }
    return { label: formatDashboardPerfLabel(line.trim()), value: '' };
}

type DashboardMetricSlug = 'production' | 'system' | 'solar-access' | 'efficiency' | 'multiplier';

function dashboardMetricSlug(label: string): DashboardMetricSlug | null {
    const normalized = label.trim().toLowerCase();
    if (normalized === 'production') return 'production';
    if (normalized === 'system size') return 'system';
    if (normalized === 'solar access') return 'solar-access';
    if (normalized === 'efficiency') return 'efficiency';
    if (normalized === 'multiplier') return 'multiplier';
    return null;
}

function DashboardMetricTileIcon({ label, size = 10 }: { label: string; size?: number }) {
    const slug = dashboardMetricSlug(label);
    const iconProps = {
        size,
        weight: 'duotone' as const,
        className: 'obd-ai-catalog-list__metric-tile-icon',
        'aria-hidden': true as const,
    };

    switch (slug) {
        case 'production':
            return <LightningIcon {...iconProps} />;
        case 'system':
            return <GaugeIcon {...iconProps} />;
        case 'solar-access':
            return <SunIcon {...iconProps} />;
        case 'efficiency':
            return <GaugeIcon {...iconProps} />;
        case 'multiplier':
            return <PercentIcon {...iconProps} />;
        default:
            return null;
    }
}

function DashboardMetricTileLabel({ label, size = 10 }: { label: string; size?: number }) {
    return (
        <span className="obd-ai-catalog-list__metric-tile-label">
            <DashboardMetricTileIcon label={label} size={size} />
            <span className="obd-ai-catalog-list__metric-tile-label-text">{label}</span>
        </span>
    );
}

/** Annual production for dashboard catalog cards (store value is kWh/yr). */
export function formatDashboardAnnualProductionLine(productionKwhYr: number | null | undefined): string {
    if (productionKwhYr == null || !Number.isFinite(productionKwhYr)) return '—';
    return `${Math.round(productionKwhYr).toLocaleString()} kWh/yr`;
}

export function buildDashboardLayoutPerfDetailLine(
    metrics?: {
        solarAccess?: number | null;
        efficiency?: number | null;
        multiplier?: number | null;
    } | null,
): string {
    const solarLabel =
        metrics?.solarAccess != null && Number.isFinite(metrics.solarAccess)
            ? `${metrics.solarAccess.toFixed(1)}%`
            : '—';
    const effLabel =
        metrics?.efficiency != null && Number.isFinite(metrics.efficiency)
            ? `${Math.round(metrics.efficiency)} %`
            : '—';
    const multiplierLabel =
        metrics?.multiplier != null && Number.isFinite(metrics.multiplier)
            ? `×${Number(metrics.multiplier).toFixed(3)}`
            : '—';
    return `Solar Access: ${solarLabel}\nEfficiency: ${effLabel}\nMultiplier: ${multiplierLabel}`;
}

export function joinDashboardCatalogDetailLines(...parts: Array<string | null | undefined>): string {
    return parts
        .flatMap((part) => (part ? splitDashboardDetailLines(part) : []))
        .filter(Boolean)
        .join('\n');
}

function flattenReactNodeText(node: React.ReactNode): string {
    if (node == null || node === false || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(flattenReactNodeText).join('');
    if (React.isValidElement(node)) return flattenReactNodeText(node.props.children);
    return '';
}

/** True when partner pricing includes a dollar amount (excludes —, N/A, and unloaded placeholders). */
export function partnerPricingNodeHasValue(node: React.ReactNode): boolean {
    const text = flattenReactNodeText(node).trim();
    if (!text) return false;
    if (text === '-' || text === '—') return false;
    if (/^N\/A$/i.test(text)) return false;
    return text.includes('$');
}

/** GoodLeap, IGS, and LightReach shown on every dashboard design card (values may load async). */
export const DASHBOARD_DEFAULT_LENDER_NAMES = ['GoodLeap', 'IGS', 'LightReach'] as const;

export type BuildDashboardLenderRowOptions = {
    /** Render these partners even before $ amounts load (—, spinner, N/A). */
    alwaysShow?: readonly string[];
};

export function buildDashboardLenderRow(
    items: { name: string; value: React.ReactNode }[],
    options?: BuildDashboardLenderRowOptions,
): React.ReactNode | null {
    const alwaysShow = new Set(options?.alwaysShow ?? []);
    const chips = items
        .filter(
            (item) => alwaysShow.has(item.name) || partnerPricingNodeHasValue(item.value),
        )
        .map((item) => (
            <span key={item.name} className="obd-ai-catalog-list__lender-chip">
                <span className="obd-ai-catalog-list__lender-chip-label">{item.name}</span>
                <span className="obd-ai-catalog-list__lender-chip-value">{item.value}</span>
            </span>
        ));
    if (chips.length === 0) return null;
    return <div className="obd-ai-catalog-list__lenders-row">{chips}</div>;
}

function partnerLineHasValue(line: string): boolean {
    const s = line.trim();
    if (!s) return false;
    if (s === '—' || s === '-') return false;
    const withoutTrailingPlaceholder = s.replace(/\s*[—–-]\s*$/u, '').trim();
    if (!withoutTrailingPlaceholder) return false;
    if (/^(GoodLeap|IGS|LightReach|GL|LR)$/i.test(withoutTrailingPlaceholder)) return false;
    return true;
}

export function DashboardAiCatalogListRow(props: DashboardAiCatalogListRowProps) {
    const {
        theme,
        rowIndex,
        cardKey,
        snapshotUrl,
        snapshotError,
        snapshotLoading,
        showNotSellableStamp,
        notSellableReason,
        label,
        summaryLine,
        prodLine,
        detailLine,
        statusLabel,
        statusVariant,
        systemKw,
        pricingLine,
        pricingContent,
        isSelected,
        isSimulating,
        selectForPricingDisabled,
        pricingBusy,
        pricingDisabled,
        onSelectForPricing,
        onActivateDesign,
        onPricing,
        onOpen3D,
        extraActions,
        trailing,
        variant = 'rail',
        emptyThumb = 'sparkle',
        disqualified,
        hideDollarAction,
        showEpcAction,
        epcBusy,
        onOpenEpc,
        hideSystemKw,
        hideChevron = false,
        showThumbIndex,
        open3dBusy = false,
        hideThumb = false,
        supplementsBelowThumb = false,
    } = props;

    const displayThumbIndex = showThumbIndex ?? variant !== 'dashboard';

    const [snapshotPreviewOpen, setSnapshotPreviewOpen] = useState(false);

    /** Skeleton only while waiting for a URL — never block rendering <img> once URL exists. */
    const showSnapshotLoading = Boolean(snapshotLoading) && !snapshotUrl;
    const idx = String(rowIndex + 1);
    const compactSummaryLine = compactSummaryText(summaryLine);
    const layoutSummary = splitSummaryWithTreeCount(compactSummaryLine);
    const compactDetailLine = compactStatsText(detailLine);
    const compactStatsLines = splitCompactStatsLines(compactDetailLine);
    const dashboardDetailLines =
        variant === 'dashboard' ? splitDashboardDetailLines(compactDetailLine) : [];
    const dashboardPerfStatLines =
        variant === 'dashboard'
            ? dashboardDetailLines.filter((line) => classifyDashboardDetailLine(line) === 'perf')
            : compactStatsLines;
    const dashboardSupplementLines =
        variant === 'dashboard'
            ? dashboardDetailLines.filter((line) => classifyDashboardDetailLine(line) === 'supplement')
            : [];
    const pricingLines = (pricingLine ?? '')
        .split(/\s*·\s*/)
        .map((s) => s.trim())
        .filter(Boolean);
    const rootMod = variant === 'dashboard' ? ' obd-ai-catalog-list__row--dashboard' : '';
    const disqMod = disqualified ? ' obd-ai-catalog-list__row--disqualified' : '';

    const railHasImage = variant === 'rail' && (Boolean(snapshotUrl) || showSnapshotLoading);
    const resolvedNotSellableReason = notSellableReason ?? formatLayoutNotSellableReason();
    const notSellableBadge =
        showNotSellableStamp && !isSimulating ? (
            <span className="obd-ai-catalog-list__not-sellable-wrap" role="status">
                <span className="obd-ai-catalog-list__not-sellable-badge">Not sellable</span>
                <Tooltip
                    title={resolvedNotSellableReason}
                    placement="top"
                    mouseEnterDelay={0.15}
                    classNames={{ root: 'obd-ai-catalog-list__not-sellable-tooltip' }}
                >
                    <span
                        role="img"
                        aria-label={resolvedNotSellableReason}
                        className="obd-ai-catalog-list__not-sellable-info"
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <InfoIcon size={12} weight="bold" aria-hidden />
                    </span>
                </Tooltip>
            </span>
        ) : null;

    const thumbIconSize = variant === 'dashboard' ? 30 : 22;
    const thumbErrorText =
        typeof snapshotError === 'string' && snapshotError.trim() ? snapshotError.trim() : '';
    const emptyThumbNode = thumbErrorText ? (
        <div className="obd-ai-catalog-list__thumb-error" role="status" title={thumbErrorText}>
            <span className="obd-ai-catalog-list__thumb-error-label">Snapshot unavailable</span>
            <span className="obd-ai-catalog-list__thumb-error-detail">{thumbErrorText}</span>
        </div>
    ) : emptyThumb === 'floppy' ? (
            <FloppyDiskIcon
                size={thumbIconSize}
                weight="duotone"
                className="obd-ai-catalog-list__thumb-sparkle"
                aria-hidden
            />
        ) : emptyThumb === 'tree' ? (
            <TreeEvergreenIcon
                size={thumbIconSize}
                weight="duotone"
                className="obd-ai-catalog-list__thumb-sparkle"
                aria-hidden
            />
        ) : (
            <SparkleIcon
                size={thumbIconSize}
                weight="duotone"
                className="obd-ai-catalog-list__thumb-sparkle"
                aria-hidden
            />
        );

    const thumbShellClass = [
        'obd-ai-catalog-list__thumb',
        snapshotUrl || showSnapshotLoading ? 'obd-ai-catalog-list__thumb--image' : '',
        thumbErrorText && !snapshotUrl && !showSnapshotLoading ? 'obd-ai-catalog-list__thumb--error' : '',
        variant === 'dashboard' ? 'obd-ai-catalog-list__thumb--dashboard obd-ai-catalog-list__thumb--dashboard-square' : '',
        railHasImage ? 'obd-ai-catalog-list__thumb--rail-image' : '',
    ]
        .filter(Boolean)
        .join(' ');

    const thumbBlock = (
        <div
            className={`obd-ai-catalog-list__visual${variant === 'dashboard' ? ' obd-ai-catalog-list__visual--dashboard' : ''}${railHasImage ? ' obd-ai-catalog-list__visual--rail-image' : ''}`}
        >
            {displayThumbIndex ? (
                <span className="obd-ai-catalog-list__index-bg" aria-hidden>
                    {String(rowIndex + 1)}
                </span>
            ) : null}
            <div className={thumbShellClass}>
                {showSnapshotLoading ? (
                    <Skeleton.Image active className="obd-ai-catalog-list__thumb-skeleton" />
                ) : snapshotUrl ? (
                    <button
                        type="button"
                        className="obd-ai-catalog-list__thumb-zoom"
                        onClick={() => setSnapshotPreviewOpen(true)}
                        aria-label={`View full layout image: ${label}`}
                    >
                        <img
                            src={snapshotUrl}
                            alt=""
                            className="obd-ai-catalog-list__thumb-img"
                        />
                    </button>
                ) : (
                    emptyThumbNode
                )}
            </div>
        </div>
    );

    const railIconSize = variant === 'dashboard' ? 19 : 16;
    const railIconWeight = (active: boolean): 'bold' | 'regular' => (active ? 'bold' : 'regular');
    const railBtnClass = (mods: string) =>
        [
            'obd-ai-slide-designs__3d-btn',
            'obd-ai-catalog-list__icon-btn',
            variant === 'dashboard' ? 'obd-dashboard-section-rail__btn obd-ai-catalog-list__rail-btn' : '',
            ...mods.split(' ').filter(Boolean),
        ]
            .filter(Boolean)
            .join(' ');

    const wrapRailIcon = (node: React.ReactNode) =>
        variant === 'dashboard' ? (
            <span className="obd-dashboard-section-rail__icon-wrap obd-ai-catalog-list__rail-icon-wrap" aria-hidden>
                {node}
            </span>
        ) : (
            node
        );

    const iconToolbarButtons = isSimulating ? null : (
        <>
            {showEpcAction && onOpenEpc ? (
                <button
                    type="button"
                    className={railBtnClass('obd-ai-catalog-list__icon-btn--epc')}
                    disabled={epcBusy}
                    title="Compare EPC by partner"
                    aria-label={`Compare EPC for all designs`}
                    onClick={onOpenEpc}
                >
                    {epcBusy ? (
                        <Spin size="small" style={{ display: 'block' }} />
                    ) : (
                        wrapRailIcon(
                            <span className="obd-ai-catalog-list__epc-btn-label" aria-hidden>
                                EPC
                            </span>,
                        )
                    )}
                </button>
            ) : null}
            {!hideDollarAction ? (
                <button
                    type="button"
                    className={railBtnClass('obd-ai-catalog-list__icon-btn--pricing')}
                    disabled={pricingDisabled}
                    title="Load financing pricing"
                    aria-label={`Load financing pricing for ${label}`}
                    onClick={onPricing}
                >
                    {pricingBusy ? (
                        <Spin size="small" style={{ display: 'block' }} />
                    ) : (
                        wrapRailIcon(
                            <CurrencyDollarIcon size={railIconSize} weight={railIconWeight(false)} aria-hidden />,
                        )
                    )}
                </button>
            ) : null}
            <button
                type="button"
                className={railBtnClass(
                    `obd-ai-catalog-list__icon-btn--open3d${isSelected && variant === 'dashboard' ? ' obd-dashboard-section-rail__btn--active' : ''}`,
                )}
                title="Open in 3D"
                aria-label={`Open ${label} in 3D`}
                disabled={open3dBusy}
                onClick={onOpen3D}
            >
                {open3dBusy ? (
                    <Spin size="small" style={{ display: 'block' }} />
                ) : (
                    wrapRailIcon(
                        <CubeIcon
                            size={railIconSize}
                            weight={railIconWeight(isSelected && variant === 'dashboard')}
                            aria-hidden
                        />,
                    )
                )}
            </button>
            {extraActions}
        </>
    );

    const showDashboardStatusPill =
        variant === 'dashboard' &&
        !isSimulating &&
        Boolean(statusLabel) &&
        !/^(ready|default)$/i.test(statusLabel.trim());

    const dashboardStatusBlock =
        variant === 'dashboard' && !isSimulating && (showDashboardStatusPill || notSellableBadge) ? (
            <div className="obd-ai-catalog-list__card-status">
                {showDashboardStatusPill ? (
                    <span className={`obd-ai-catalog-list__pill${pillClass(statusVariant)}`}>{statusLabel}</span>
                ) : null}
                {notSellableBadge}
            </div>
        ) : null;

    const actionsClass =
        variant === 'dashboard'
            ? 'obd-ai-catalog-list__actions obd-ai-catalog-list__actions--card-rail'
            : 'obd-ai-catalog-list__actions';

    const actionsBlock = (
        <div className={actionsClass}>
            {isSimulating ? <span className="obd-ai-slide-designs__3d-placeholder" aria-hidden /> : iconToolbarButtons}
        </div>
    );

    const metricChipClassName = `obd-ai-catalog-list__paired-metric-chip obd-ai-catalog-list__paired-metric-chip--${theme}`;

    const dashboardMetricsLine =
        variant === 'dashboard' && (dashboardPerfStatLines.length > 0 || dashboardSupplementLines.length > 0)
            ? [...dashboardPerfStatLines, ...dashboardSupplementLines]
                  .map((line) => line.replace(/:\s*/g, ' '))
                  .join(' · ')
            : '';

    const dashboardPartnerLines =
        variant === 'dashboard' && pricingLine && !pricingContent
            ? (pricingLines.length ? pricingLines : [pricingLine]).filter(partnerLineHasValue)
            : [];

    const dashboardLayoutValue = [
        layoutSummary.baseText,
        layoutSummary.treeCount != null ? `${layoutSummary.treeCount} trees` : null,
    ]
        .filter(Boolean)
        .join(' · ');

    const showHeroMetrics =
        variant === 'dashboard' && Boolean(prodLine || (!hideSystemKw && systemKw && systemKw !== '—'));

    const lenderRow =
        pricingContent ??
        (dashboardPartnerLines.length > 0 ? (
            <div className="obd-ai-catalog-list__lenders-row">
                {dashboardPartnerLines.map((line) => (
                    <span key={line} className="obd-ai-catalog-list__lender-chip obd-ai-catalog-list__lender-chip--text">
                        {line}
                    </span>
                ))}
            </div>
        ) : null);

    const showDashboardMetrics =
        dashboardPerfStatLines.length > 0 ||
        (showHeroMetrics && (prodLine || (!hideSystemKw && systemKw && systemKw !== '—')));

    const showSupplementsInCardBody = !supplementsBelowThumb && dashboardSupplementLines.length > 0;

    const supplementChipsBlock =
        dashboardSupplementLines.length > 0 ? (
            <div className="obd-ai-catalog-list__card-detail-supplements" aria-label="Scenario details">
                {dashboardSupplementLines.map((line) => (
                    <span
                        key={line}
                        className="obd-ai-catalog-list__card-detail-supplement-chip"
                        title={line}
                    >
                        {line}
                    </span>
                ))}
            </div>
        ) : null;

    const showDashboardDetails =
        variant === 'dashboard' &&
        Boolean(lenderRow || showSupplementsInCardBody || showDashboardMetrics);

    const dashboardDetailsAriaLabel =
        [
            lenderRow ? 'Partner pricing' : null,
            dashboardMetricsLine ? `Details: ${dashboardMetricsLine}` : null,
            showHeroMetrics ? `Production ${prodLine || '—'}, system ${systemKw || '—'} kW` : null,
        ]
            .filter(Boolean)
            .join('; ') || 'Design specifications';

    const handleNameClick = onActivateDesign ?? onSelectForPricing;

    return (
        <div
            key={cardKey}
            role="listitem"
            className={`obd-ai-catalog-list__row${isSelected ? ' obd-ai-catalog-list__row--selected' : ''}${rootMod}${disqMod}`}
        >
            {variant === 'dashboard' ? (
                <div
                    className={`obd-ai-catalog-list__row-inner obd-ai-catalog-list__row-inner--spread obd-ai-catalog-list__row-inner--card-minimal${
                        hideThumb ? ' obd-ai-catalog-list__row-inner--no-visual' : ''
                    }${
                        supplementsBelowThumb && dashboardSupplementLines.length > 0
                            ? ' obd-ai-catalog-list__row-inner--supplements-below-thumb'
                            : ''
                    }`}
                >
                    {!hideThumb ? (
                        supplementsBelowThumb && supplementChipsBlock ? (
                            <div className="obd-ai-catalog-list__thumb-column">
                                {thumbBlock}
                                <div className="obd-ai-catalog-list__thumb-supplements">{supplementChipsBlock}</div>
                            </div>
                        ) : (
                            thumbBlock
                        )
                    ) : null}
                    {!hideThumb ? (
                        <div className="obd-ai-catalog-list__thumb-mobile-chips" role="group" aria-label="Production and system size">
                            <span className="obd-ai-catalog-list__thumb-mobile-chip" title={prodLine}>
                                {prodLine}
                            </span>
                            {!hideSystemKw ? (
                                <span className="obd-ai-catalog-list__thumb-mobile-chip">
                                    {systemKw}
                                    <span className="obd-ai-catalog-list__metric-unit"> kW</span>
                                </span>
                            ) : null}
                        </div>
                    ) : null}
                    <div className="obd-ai-catalog-list__spread-main">
                        {!isSimulating ? (
                            <>
                                <button
                                    type="button"
                                    className={`obd-ai-catalog-list__name obd-ai-catalog-list__name--mobile-top${isSelected ? ' obd-ai-catalog-list__name--selected' : ''}`}
                                    disabled={selectForPricingDisabled}
                                    onClick={handleNameClick}
                                >
                                    {label}
                                </button>
                                <span className="obd-ai-catalog-list__mobile-top-summary">
                                    {layoutSummary.baseText}
                                    {layoutSummary.treeCount != null ? (
                                        <span className="obd-ai-catalog-list__tree-count" title={`${layoutSummary.treeCount} trees`}>
                                            <TreeEvergreenIcon size={12} weight="fill" aria-hidden />
                                            <span>{layoutSummary.treeCount}</span>
                                        </span>
                                    ) : null}
                                </span>
                            </>
                        ) : null}
                        {isSimulating ? (
                            <span className="obd-ai-catalog-list__sim">Simulating…</span>
                        ) : (
                            <div className="obd-ai-catalog-list__card-body">
                                <div className="obd-ai-catalog-list__card-head">
                                    <div className="obd-ai-catalog-list__design-title-row">
                                        <button
                                            type="button"
                                            className={`obd-ai-catalog-list__name obd-ai-catalog-list__name--primary${isSelected ? ' obd-ai-catalog-list__name--selected' : ''}`}
                                            disabled={selectForPricingDisabled}
                                            onClick={handleNameClick}
                                        >
                                            {label}
                                        </button>
                                        {layoutSummary.baseText || layoutSummary.treeCount != null ? (
                                            <div
                                                className="obd-ai-catalog-list__card-meta-row"
                                                aria-label={`Layout: ${dashboardLayoutValue || layoutSummary.baseText}`}
                                            >
                                                {layoutSummary.baseText ? (
                                                    <span className="obd-ai-catalog-list__card-meta-chip obd-ai-catalog-list__card-meta-chip--layout">
                                                        {layoutSummary.baseText}
                                                    </span>
                                                ) : null}
                                                {layoutSummary.treeCount != null ? (
                                                    <span
                                                        className="obd-ai-catalog-list__card-meta-chip obd-ai-catalog-list__card-meta-chip--trees"
                                                        title={`${layoutSummary.treeCount} trees`}
                                                    >
                                                        <TreeEvergreenIcon size={11} weight="fill" aria-hidden />
                                                        <span>{layoutSummary.treeCount}</span>
                                                    </span>
                                                ) : null}
                                            </div>
                                        ) : null}
                                    </div>
                                </div>
                                {showDashboardDetails ? (
                                    <>
                                        {showDashboardMetrics ? (
                                            <div
                                                className="obd-ai-catalog-list__card-details-metrics obd-ai-catalog-list__card-details-metrics--cockpit"
                                                aria-label={dashboardDetailsAriaLabel}
                                                title={compactDetailLine}
                                            >
                                                {prodLine ? (
                                                    <div
                                                        className="obd-ai-catalog-list__card-metrics-hero"
                                                        role="group"
                                                        aria-label={`Production ${prodLine}`}
                                                    >
                                                        <div
                                                            className="obd-ai-catalog-list__metric-tile obd-ai-catalog-list__metric-tile--hero obd-ai-catalog-list__metric-tile--production"
                                                            title={prodLine}
                                                        >
                                                            <DashboardMetricTileLabel label="Production" size={12} />
                                                            <span className="obd-ai-catalog-list__metric-tile-value obd-ai-catalog-list__metric-tile-value--production">
                                                                {prodLine}
                                                            </span>
                                                        </div>
                                                    </div>
                                                ) : null}
                                                {showHeroMetrics &&
                                                ((!hideSystemKw && systemKw && systemKw !== '—') ||
                                                    dashboardPerfStatLines.length > 0) ? (
                                                    <div
                                                        className="obd-ai-catalog-list__card-metrics-secondary"
                                                        aria-label="Performance metrics"
                                                    >
                                                        {!hideSystemKw && systemKw && systemKw !== '—' ? (
                                                            <div
                                                                className="obd-ai-catalog-list__metric-tile obd-ai-catalog-list__metric-tile--perf obd-ai-catalog-list__metric-tile--system"
                                                                title={`${systemKw} kW`}
                                                            >
                                                                <DashboardMetricTileLabel label="System size" size={10} />
                                                                <span className="obd-ai-catalog-list__metric-tile-value">
                                                                    {systemKw}
                                                                    <span className="obd-ai-catalog-list__metric-unit">
                                                                        {' '}
                                                                        kW
                                                                    </span>
                                                                </span>
                                                            </div>
                                                        ) : null}
                                                        {dashboardPerfStatLines.map((line) => {
                                                            const { label, value } = parseDashboardStatLine(line);
                                                            const metricSlug = dashboardMetricSlug(label);
                                                            return (
                                                                <div
                                                                    key={line}
                                                                    className={[
                                                                        'obd-ai-catalog-list__metric-tile',
                                                                        'obd-ai-catalog-list__metric-tile--perf',
                                                                        metricSlug
                                                                            ? `obd-ai-catalog-list__metric-tile--${metricSlug}`
                                                                            : '',
                                                                    ]
                                                                        .filter(Boolean)
                                                                        .join(' ')}
                                                                    title={line.replace(/:\s*/g, ' ')}
                                                                >
                                                                    <DashboardMetricTileLabel label={label} size={10} />
                                                                    {value ? (
                                                                        <span className="obd-ai-catalog-list__metric-tile-value">
                                                                            {value}
                                                                        </span>
                                                                    ) : null}
                                                                </div>
                                                            );
                                                        })}
                                                    </div>
                                                ) : null}
                                            </div>
                                        ) : null}
                                        {showSupplementsInCardBody ? supplementChipsBlock : null}
                                        {lenderRow ? (
                                            <div
                                                className="obd-ai-catalog-list__card-spec-col obd-ai-catalog-list__card-spec-col--lenders obd-ai-catalog-list__card-spec-col--lenders-bottom"
                                                aria-label="Partner pricing"
                                            >
                                                {lenderRow}
                                            </div>
                                        ) : null}
                                    </>
                                ) : null}
                                {!isSimulating ? (
                                    <div className="obd-ai-catalog-list__card-bottom-band">
                                        <div className="obd-ai-catalog-list__card-bottom-actions">
                                            {dashboardStatusBlock}
                                            <div className="obd-ai-catalog-list__card-rail obd-ai-catalog-list__card-rail--footer">
                                                {actionsBlock}
                                            </div>
                                        </div>
                                    </div>
                                ) : null}
                            </div>
                        )}
                        <div className="obd-ai-catalog-list__bottom-actions obd-ai-catalog-list__card-footer obd-ai-catalog-list__card-footer--legacy-mobile">
                            {!isSimulating ? (
                                <button
                                    type="button"
                                    className={`obd-ai-catalog-list__name obd-ai-catalog-list__name--mobile-relocated${isSelected ? ' obd-ai-catalog-list__name--selected' : ''}`}
                                    disabled={selectForPricingDisabled}
                                    onClick={handleNameClick}
                                >
                                    {label}
                                </button>
                            ) : null}
                            <div className="obd-ai-catalog-list__mobile-bottom-chips" role="group" aria-label="Production and system">
                                <span className="obd-ai-catalog-list__mobile-production-chip" title={prodLine}>
                                    {prodLine}
                                </span>
                                {!hideSystemKw ? (
                                    <span className="obd-ai-catalog-list__mobile-system-chip">
                                        {systemKw}
                                        <span className="obd-ai-catalog-list__metric-unit"> kW</span>
                                    </span>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div
                    className={`obd-ai-catalog-list__row-inner${railHasImage ? ' obd-ai-catalog-list__row-inner--rail-image' : ''}`}
                >
                    {thumbBlock}
                    <div className="obd-ai-catalog-list__name-block">
                        {isSimulating ? (
                            <span className="obd-ai-catalog-list__sim">Simulating…</span>
                        ) : (
                            <button
                                type="button"
                                className={`obd-ai-catalog-list__name${isSelected ? ' obd-ai-catalog-list__name--selected' : ''}`}
                                disabled={selectForPricingDisabled}
                                onClick={handleNameClick}
                            >
                                {label}
                            </button>
                        )}
                        <p className="obd-ai-catalog-list__desc">
                            {layoutSummary.baseText}
                            {layoutSummary.treeCount != null ? (
                                <span className="obd-ai-catalog-list__tree-count" title={`${layoutSummary.treeCount} trees`}>
                                    <TreeEvergreenIcon size={12} weight="fill" aria-hidden />
                                    <span>{layoutSummary.treeCount}</span>
                                </span>
                            ) : null}
                        </p>
                        {detailLine ? <p className="obd-ai-catalog-list__desc obd-ai-catalog-list__desc--detail">{compactDetailLine}</p> : null}
                        <div className="obd-ai-catalog-list__paired-metrics" role="group" aria-label="Production and system size">
                            <span className={metricChipClassName} title={prodLine}>
                                {prodLine}
                            </span>
                            {!hideSystemKw ? (
                                <span className={metricChipClassName}>
                                    {systemKw}
                                    <span className="obd-ai-catalog-list__metric-unit"> kW</span>
                                </span>
                            ) : null}
                        </div>

                        <div className="obd-ai-catalog-list__tail">
                            <div className="obd-ai-catalog-list__tail-status">
                                <span className={`obd-ai-catalog-list__pill${pillClass(statusVariant)}`}>{statusLabel}</span>
                                {notSellableBadge}
                            </div>
                            {actionsBlock}
                        </div>
                    </div>
                </div>
            )}
            {trailing}
            {snapshotUrl ? (
                <Modal
                    open={snapshotPreviewOpen}
                    onCancel={() => setSnapshotPreviewOpen(false)}
                    footer={null}
                    centered
                    destroyOnHidden
                    width="min(96vw, 1120px)"
                    zIndex={10250}
                    title={label}
                    rootClassName={`obd-ai-snapshot-preview-modal obd-theme-${theme}`}
                    classNames={{ body: 'obd-ai-snapshot-preview-modal__body' }}
                >
                    <div className="obd-ai-snapshot-preview-modal__scroll">
                        <img
                            src={snapshotUrl}
                            alt={label}
                            className="obd-ai-snapshot-preview-modal__img"
                        />
                    </div>
                </Modal>
            ) : null}
        </div>
    );
}
